import { AttributeOption, AttributeOptionValue } from 'models/AttributeOption';
import {
  createContext,
  createElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { ConfiguratorProps, ModalProps } from 'views/ProductPage/configurators/index';
import { ConfiguratorBase } from 'models/ConfiguratorBase';
import { IConstraint } from 'models/IConstraint';
import { Requires } from 'models/Constraints';
import { ConstraintType } from 'models/ConstraintType';

export type Attribute = AttributeOption & { selected: string | undefined };

interface ConfiguratorCtx<Config extends ConfiguratorBase = ConfiguratorBase> {
  configurator: Config;
  attributes: Attribute[];
  onSelectChange: OnSelectChange;
}
const ConfigCtx = createContext<ConfiguratorCtx>({} as ConfiguratorCtx);

export function useConfigurator<C extends ConfiguratorBase = ConfiguratorBase>() {
  return useContext(ConfigCtx) as ConfiguratorCtx<C>;
}

const isRequires = (c: IConstraint): c is Requires => c.rule === ConstraintType.Requires;

export const useVisibility = () => {
  const { configurator } = useConfigurator();
  const requires: Requires[] = useMemo(
    () => configurator.constraints.filter(isRequires),
    [configurator.constraints]
  );

  return useCallback(
    function (id: string) {
      if (!requires) {
        return true;
      }
      const constraint = requires.find(r => r.attribute === id);
      if (!constraint) {
        return true;
      }
      return constraint.attributes.every(a =>
        Boolean(configurator.values && configurator.values[a])
      );
    },
    [configurator.values, requires]
  );
};

export const ConfigProvider = ({
  children,
  ...props
}: UseConfigurator & { children: ReactNode }) => {
  const [attributes, onSelectChange] = useSetupConfigurator(props);
  return createElement(
    ConfigCtx.Provider,
    { value: { configurator: props.configurator, attributes, onSelectChange } },
    children
  );
};

function permutations(attributes: Attribute[], variants: ConfiguratorBase['variants']) {
  const attrs = attributes.filter(a => a.required);
  const selected = attrs
    .filter(({ selected }) => Boolean(selected))
    .map(({ id, selected }) => ({ id, value: selected }));

  function permutation([id, values]: [string, string[]]): {
    [key: string]: string[];
  } {
    const s = selected.filter(s => s.id !== id);
    const combinations = values.map(value => s.concat([{ id, value }]));
    const variant = Object.values(variants).filter(variant =>
      combinations.some(combination =>
        combination.every(({ id, value }) => variant[id] && variant[id].some(v => value === v))
      )
    );
    return {
      [id]: [...new Set(variant.map(v => v[id]).reduce((acc, curr) => [...acc, ...curr], []))]
    };
  }
  return attrs
    .map(({ id, values }) => [id, values.map(v => v.value)])
    .map(permutation)
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});
}

const selectAttributeMap =
  (id: Attribute['id'], value?: AttributeOptionValue['value']) => (attr: Attribute) =>
    attr.id !== id
      ? attr
      : {
          ...attr,
          selected: value
        };

type UseConfigurator = ConfiguratorProps &
  ModalProps & {
    configurator: ConfiguratorBase;
  };

interface OnChangeOptions {
  emit?: boolean;
  setSelectedOnReset?: boolean;
}

type OnSelectChange = (id: string, value?: string, options?: OnChangeOptions) => void;
type ConfiguratorLike = [Attribute[], OnSelectChange];

const useSetupConfigurator = ({
  onSelectValue,
  onVariant,
  reset,
  configurator
}: UseConfigurator): ConfiguratorLike => {
  const [dirty, setDirty] = useState(false);
  const [current, setCurrent] = useState('');
  const init = useCallback(
    (reset?: boolean) =>
      configurator.attributes.reduce(
        (acc, attr) => [
          ...acc,
          {
            ...attr,
            selected: reset
              ? undefined
              : (attr.values.find(v => v.selected) || { value: undefined }).value
          }
        ],
        []
      ),
    [configurator.attributes]
  );

  const [attributes, setAttribute] = useState<Attribute[]>(init());
  const combinations = useMemo(
    () => permutations(attributes, configurator.variants),
    [attributes, configurator.variants]
  );

  useEffect(() => {
    const unSelected: { [key: string]: Attribute } = attributes
      .filter(value => value.required && value.selected === undefined)
      .reduce((acc, attr) => ({ ...acc, [attr.id]: attr }), {});

    if (
      Object.keys(unSelected).length > 0 &&
      Object.entries(unSelected).every(([id]) => combinations[id].length === 1)
    ) {
      setAttribute(
        attributes.map(attr =>
          !unSelected[attr.id]
            ? attr
            : {
                ...attr,
                selected: combinations[attr.id][0]
              }
        )
      );

      attributes
        .filter(attr => unSelected[attr.id])
        .map(attr => [attr.id, combinations[attr.id][0]])
        .forEach(([id, value]) => onSelectValue(id, value));
    }
  }, [attributes, combinations, onSelectValue]);

  useEffect(() => {
    const required = attributes.filter(attr => attr.required);

    if (!dirty || !required.every(attr => Boolean(attr.selected))) {
      return;
    }

    const [id] = Object.entries(configurator.variants).find(([, variants]) =>
      required.every(({ id, selected }) => {
        return variants[id] && variants[id].includes(selected as string);
      })
    ) || [undefined];

    if (id && id !== current) {
      setCurrent(id);
      onVariant && onVariant(id);
    }
  }, [current, setCurrent, dirty, attributes, configurator.variants, onVariant]);

  const onSelectChange = useCallback(
    (
      id: string,
      value?: string,
      { emit, setSelectedOnReset } = { emit: true, setSelectedOnReset: false }
    ) => {
      const attr = attributes.find(attr => attr.id === id);
      if (
        attr &&
        attr.required &&
        combinations[id] &&
        typeof value === 'string' &&
        !combinations[id].includes(value)
      ) {
        setDirty(false);
        reset({ showModal: !setSelectedOnReset });
        let attribute = init(true);
        if (setSelectedOnReset) {
          attribute = attribute.map(selectAttributeMap(id, value));
        }
        setAttribute(attribute);
        if (setSelectedOnReset && emit) {
          onSelectValue(id, value);
        }
        return;
      }

      setDirty(true);
      setAttribute(a => a.map(selectAttributeMap(id, value)));

      if (emit) {
        onSelectValue(id, value);
      }
    },
    [attributes, combinations, init, onSelectValue, reset]
  );

  const state = attributes.reduce(
    (acc, attr) => [
      ...acc,
      {
        ...attr,
        values: attr.values.map(v => ({
          ...v,
          disabled:
            attr.required && combinations[attr.id] && !combinations[attr.id].includes(v.value)
        }))
      }
    ],
    []
  );

  return [state, onSelectChange];
};
